/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { StyleSheet } from '@coursera/coursera-ui';

import HowItWorksInformationCard from 'bundles/program-common/components/StaticInformationCards/HowItWorksInformationCard';
import MyRecentActivityForMultiprogram from 'bundles/program-home/components/enterprise-home/MyRecentActivityForMultiprogram';
import PendingInvitations from 'bundles/program-home/components/enterprise-home/PendingInvitations';
import ProgramContainer from 'bundles/program-home/components/enterprise-home/ProgramContainer';
import type {
  EnterpriseHomePageQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_enterprisePrograms_elements as EnterpriseProgram,
  EnterpriseHomePageQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_invitations_elements as ProgramInvitation,
} from 'bundles/program-home/components/enterprise-home/__generated__/EnterpriseHomePageQuery';
import CatalogSection from 'bundles/program-home/components/single-program/CatalogSection';
import type { OnProductCardClick } from 'bundles/program-home/types/Products';

type PropsFromCaller = {
  isVisible: boolean;
  enterprisePrograms: EnterpriseProgram[];
  programInvitations: ProgramInvitation[];
  onProductCardClick: OnProductCardClick;
  isAuthenticatedUser: boolean;
  hasTSPs: boolean;
  catalogHelpCardId: string;
  catalogHelpIsExpanded: boolean;
  toggleCatalogHelpIsExpanded: () => void;
  thirdPartyOrganizationId: string;
  thirdPartyOrgSlug: string;
  userId: number;
  enableAcademicDisciplines?: boolean;
  allowOrgForCurriculumBuilder?: boolean;
  allowOrgForSpecializationConfiguration?: boolean;
  showSkillSetRoleFilter: boolean;
};

type Props = PropsFromCaller;

const oldAndStinkyAphroditeStyles = StyleSheet.create({
  infoCard: {
    margin: '48px auto 48px',
    maxWidth: '1200px',
  },
});

export default function HomeTabContent({
  isVisible,
  enterprisePrograms,
  programInvitations,
  onProductCardClick,
  isAuthenticatedUser,
  hasTSPs,
  catalogHelpCardId,
  catalogHelpIsExpanded,
  toggleCatalogHelpIsExpanded,
  thirdPartyOrganizationId,
  thirdPartyOrgSlug,
  userId,
  enableAcademicDisciplines,
  allowOrgForCurriculumBuilder,
  allowOrgForSpecializationConfiguration,
  showSkillSetRoleFilter,
}: Props) {
  const styles = {
    container: css`
      // Align CatalogSection margins with other tabs / footer
      .CatalogSection:first-child .CatalogSection-Margins {
        margin-top: 2.75rem;
      }

      .CatalogSection:last-child .CatalogSection-Margins {
        margin-bottom: 2.75rem;
      }
    `,
  };

  return (
    <div className="rc-HomeTabContent" css={styles.container}>
      <CatalogSection>
        <HowItWorksInformationCard
          rootClassName={oldAndStinkyAphroditeStyles.infoCard}
          id={catalogHelpCardId}
          isOpen={catalogHelpIsExpanded}
          onRequestClose={toggleCatalogHelpIsExpanded}
          userAuthenticated={isAuthenticatedUser}
          hasTSPs={hasTSPs}
        />
        <PendingInvitations programInvitations={programInvitations} />
        <MyRecentActivityForMultiprogram
          thirdPartyOrganizationId={thirdPartyOrganizationId}
          thirdPartyOrgSlug={thirdPartyOrgSlug}
          userId={userId}
        />
      </CatalogSection>

      {enterprisePrograms.map((enterpriseProgram, index) => (
        <CatalogSection key={enterpriseProgram.id}>
          <ProgramContainer
            enterpriseProgram={enterpriseProgram}
            isAuthenticatedUser={Boolean(userId)}
            onProductCardClick={onProductCardClick}
            lazy={index > 0}
            isVisible={isVisible}
            enableAcademicDiscipline={
              enableAcademicDisciplines && Boolean(enterpriseProgram.metadata?.linkedAcademicDisciplineIds?.length)
            }
            allowOrgForCurriculumBuilder={allowOrgForCurriculumBuilder}
            allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
            showSkillSetRoleFilter={showSkillSetRoleFilter}
          />
        </CatalogSection>
      ))}
    </div>
  );
}
